/**
 * The two 48px squares outside the panel, each captioned with the movement it steps to.
 *
 * ── Outside the panel, and that is what decides the panel's width ───────────────────────────
 * The canon puts them in the margin either side of the card rather than inside its bar, so the
 * picture keeps the whole width. `--w-ex-theatre-gutter` is what the panel leaves for them, and the
 * panel's own `max-width` reads it — so on a narrow window the card shrinks and the squares stay on
 * screen, rather than the card keeping its 1240 and the walk sliding off the edge.
 *
 * ── The caption is the POINT ────────────────────────────────────────────────────────────────
 * The list wraps at both ends, and a bare chevron that silently jumped from the last movement to
 * the first would read as a bug. With the neighbour's name under the square the wrap is a fact the
 * trainer can see before they press it. The name is also the control's accessible name, so the two
 * channels say the same thing.
 *
 * ── It is DRAWN only where there is somewhere to go ─────────────────────────────────────────
 * `neighboursOf` answers `null` for a list of one and for a movement a filter has taken off the
 * list under an open card. Both are the same fact — this card has no position to step from — and a
 * pair of squares captioned with the name already on the bar would be controls that claim to act
 * and do not.
 */

import { useTranslation } from 'react-i18next';

import ghost from '~/shared/ui/ghostButton.module.css';

import type { TheatreNeighbours } from '../model/theatreNeighbours';

import { ChevronLeftGlyph, ChevronRightGlyph } from './ExerciseLibraryIcons';
import styles from './TheatreWalk.module.css';

interface TheatreWalkProps {
  readonly neighbours: TheatreNeighbours;
  readonly onStep: (exerciseId: number) => void;
}

export function TheatreWalk({ neighbours, onStep }: TheatreWalkProps) {
  const { t } = useTranslation();

  return (
    <>
      <div className={`${String(styles.walk)} ${String(styles.previous)}`}>
        <button
          type="button"
          className={`${String(ghost.ghost)} ${String(styles.square)}`}
          aria-label={t('web:exerciseLibrary.theatre.previous', {
            name: neighbours.previous.name,
          })}
          onClick={() => {
            onStep(neighbours.previous.id);
          }}
        >
          <ChevronLeftGlyph className={styles.glyph} />
        </button>
        {/* Decorative: the square above it already carries this name in its own label, and a caption
          * announced twice is a trainer hearing every movement's name three times over. */}
        <span className={styles.caption} aria-hidden>
          {neighbours.previous.name}
        </span>
      </div>

      <div className={`${String(styles.walk)} ${String(styles.next)}`}>
        <button
          type="button"
          className={`${String(ghost.ghost)} ${String(styles.square)}`}
          aria-label={t('web:exerciseLibrary.theatre.next', { name: neighbours.next.name })}
          onClick={() => {
            onStep(neighbours.next.id);
          }}
        >
          <ChevronRightGlyph className={styles.glyph} />
        </button>
        <span className={styles.caption} aria-hidden>
          {neighbours.next.name}
        </span>
      </div>
    </>
  );
}
